import { useCallback, useEffect, useRef, useState } from "react";
import {
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import { categoryMap } from "../../config/CategoryMap";
import { ROUTES, buildRoute } from "../../routes/RouteNames";

import ButtonSubcategory from "../../components/ButtonSubcategory";
import JobItem from "../../components/JobItem";
import JobItemSkeleton from "../../components/JobItemSkeleton";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import Path from "../../components/Path";
import RadiusSelect, { DEFAULT_RADIUS } from "../../components/RadiusSelect";

import "../Workers/Listing.css";
import "./JobsListing.css";

import { format, startOfToday } from "date-fns";
import { cs as dateFnsCs } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import { cs as dayPickerCs } from "react-day-picker/locale/cs";
import "react-day-picker/style.css";

import {
    faCalendar,
    faHandFist,
    faLightbulb,
    faMagnifyingGlass,
    faVideo,
    faWrench,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axiosInstance from "../../api/axiosInstance";

const CZ_WEEKDAY_ABBR = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];

const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.slice(0, 10).split("-").map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
        ? date
        : null;
};

const formatDayHeader = (dateStr) => {
    const date = parseLocalDate(dateStr);
    if (!date) return null;
    return `${CZ_WEEKDAY_ABBR[date.getDay()]} ${format(date, "d. M.")}`;
};

const DateRangePicker = ({ dateFrom, dateTo, onChangeFrom, onChangeTo, durationType }) => {
    const [openFrom, setOpenFrom] = useState(false);
    const [openTo, setOpenTo] = useState(false);
    const fromRef = useRef(null);
    const toRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openFrom && !fromRef.current?.contains(event.target))
                setOpenFrom(false);
            if (openTo && !toRef.current?.contains(event.target))
                setOpenTo(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [openFrom, openTo]);

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        try {
            return format(new Date(dateStr), "d. MMMM yyyy", {
                locale: dateFnsCs,
            });
        } catch {
            return null;
        }
    };

    const fromDate = dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined;
    const toDate = dateTo ? new Date(`${dateTo}T00:00:00`) : undefined;
    const dateRangeModifiers = {
        ...(fromDate && { range_start: fromDate }),
        ...(toDate && { range_end: toDate }),
        ...(fromDate &&
            toDate &&
            fromDate < toDate && {
                range_middle: { after: fromDate, before: toDate },
            }),
    };

    return (
        <div className="date-range-picker">
            <div className="date-range-picker_field" ref={fromRef}>
                <label className="body_base label-move">
                    {durationType === "long_term" ? "Datum nástupu" : "Datum od"}
                </label>
                <div
                    className={`picker-input-display ${openFrom ? "open" : ""}`}
                    onClick={() => setOpenFrom(!openFrom)}
                >
                    <span
                        className={`picker-input-text ${!dateFrom ? "picker-input-placeholder" : ""}`}
                    >
                        {formatDate(dateFrom) ||
                            (durationType === "long_term" ? "Datum nástupu" : "Od data")}
                    </span>
                    <FontAwesomeIcon icon={faCalendar} className="picker-input-icon" />
                </div>
                {openFrom && (
                    <div className="picker-popover">
                        <DayPicker
                            mode="single"
                            selected={dateFrom ? new Date(dateFrom) : undefined}
                            onSelect={(date) => {
                                const iso = date
                                    ? format(date, "yyyy-MM-dd")
                                    : "";
                                onChangeFrom(iso);
                                if (dateTo && iso > dateTo) {
                                    onChangeTo(iso);
                                }
                                setOpenFrom(false);
                            }}
                            disabled={{ before: startOfToday() }}
                            modifiers={dateRangeModifiers}
                            modifiersClassNames={{
                                range_start: "date-range-start",
                                range_middle: "date-range-middle",
                                range_end: "date-range-end",
                            }}
                            locale={dayPickerCs}
                            showOutsideDays
                            fixedWeeks
                            initialFocus={false}
                        />
                    </div>
                )}
            </div>

            {durationType !== "long_term" && (
                <div className="date-range-picker_field" ref={toRef}>
                    <label className="body_base label-move">Datum do</label>
                    <div
                        className={`picker-input-display ${openTo ? "open" : ""}`}
                        onClick={() => setOpenTo(!openTo)}
                    >
                        <span
                            className={`picker-input-text ${!dateTo ? "picker-input-placeholder" : ""}`}
                        >
                            {formatDate(dateTo) || "Do data"}
                        </span>
                        <FontAwesomeIcon icon={faCalendar} className="picker-input-icon" />
                    </div>
                    {openTo && (
                        <div className="picker-popover">
                            <DayPicker
                                mode="single"
                                selected={dateTo ? new Date(dateTo) : undefined}
                                onSelect={(date) => {
                                    const iso = date
                                        ? format(date, "yyyy-MM-dd")
                                        : "";
                                    onChangeTo(iso);
                                    setOpenTo(false);
                                }}
                                disabled={{
                                    before: fromDate || startOfToday(),
                                }}
                                modifiers={dateRangeModifiers}
                                modifiersClassNames={{
                                    range_start: "date-range-start",
                                    range_middle: "date-range-middle",
                                    range_end: "date-range-end",
                                }}
                                locale={dayPickerCs}
                                showOutsideDays
                                fixedWeeks
                                initialFocus={false}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Jobs = () => {
    const { subcategory } = useParams();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const path = useLocation();
    const navigate = useNavigate();

    // Stav stránkování
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const isFetching = useRef(false);

    // Stav filtrů
    const [searchTerm, setSearchTerm] = useState("");
    const [location, setLocation] = useState(null);
    const [radius, setRadius] = useState(DEFAULT_RADIUS);

    // Řidičský průkaz
    const [driverLicense, setDriverLicense] = useState("all");
    const [isOpenLicense, setIsOpenLicense] = useState(false);
    const selectRefLicense = useRef(null);

    const LICENSE_OPTIONS = [
        { value: "all", label: "Nerozhoduje" },
        { value: "none", label: "Bez požadavku" },
        { value: "B", label: "Skupina B" },
        { value: "C", label: "Skupina C" },
        { value: "D", label: "Skupina D" },
        { value: "E", label: "Skupina E" },
    ];

    // Od dneška se zobrazují nabídky s datem nástupu od dnešního dne.
    const [dateFrom, setDateFrom] = useState(() =>
        format(new Date(), "yyyy-MM-dd"),
    );
    const [dateTo, setDateTo] = useState("");

    // Spolupráce (duration_type)
    const [durationType, setDurationType] = useState({
        value: "single",
        label: "Jednorázová akce",
    });
    const [isOpenDuration, setIsOpenDuration] = useState(false);
    const selectRefDuration = useRef(null);

    const DURATION_OPTIONS = [
        { value: "single", label: "Jednorázová akce" },
        { value: "long_term", label: "Dlouhodobá spolupráce" },
    ];

    // Zobrazit (profile_match)
    const [displayMode, setDisplayMode] = useState({
        value: "all",
        label: "Zobrazit vše",
    });
    const [isOpenDisplay, setIsOpenDisplay] = useState(false);
    const selectRefDisplay = useRef(null);

    const DISPLAY_OPTIONS = [
        { value: "all", label: "Zobrazit vše" },
        { value: "profile_match", label: "Relevantní pro mě" },
    ];

    // Komunita
    const [networkOnly, setNetworkOnly] = useState(false);
    const [verifiedOnly, setVerifiedOnly] = useState(false);

    // FUNKCE PRO NAČÍTNÍ DAT
    const fetchJobs = useCallback(
        async (pageNumber, isInitial = false) => {
            if (isFetching.current && !isInitial) return;
            isFetching.current = true;

            try {
                const response = await axiosInstance.get(
                    "/api/jobs-listings",
                    {
                        params: {
                            subcategory,
                            location: location?.label || "",
                            lat: location?.latitude ?? "",
                            lng: location?.longitude ?? "",
                            radius: radius,
                            search: searchTerm,
                            ...(driverLicense !== "all" && { driver_license: driverLicense }),
                            duration_type: durationType.value,
                            date_from: dateFrom || undefined,
                            date_to: dateTo || undefined,
                            network_only: networkOnly,
                            verified_only: verifiedOnly,
                            profile_match: displayMode.value === "profile_match",
                            page: pageNumber,
                        },
                    },
                );

                const newJobs = response.data.data || [];

                setJobs((prev) => {
                    if (isInitial) return newJobs;
                    const existingIds = new Set(prev.map((j) => j.id));
                    const uniqueNewJobs = newJobs.filter(
                        (j) => !existingIds.has(j.id),
                    );
                    return [...prev, ...uniqueNewJobs];
                });

                setHasMore(response.data.next_page_url !== null);
            } catch (error) {
                console.error("Chyba:", error);
            } finally {
                setLoading(false);
                isFetching.current = false;
            }
        },
        [
            subcategory,
            location,
            radius,
            searchTerm,
            driverLicense,
            durationType.value,
            dateFrom,
            dateTo,
            networkOnly,
            verifiedOnly,
            displayMode.value,
        ],
    );

    // Reset a první načtení při změně filtrů
    useEffect(() => {
        setPage(1);
        setHasMore(true);

        setJobs([]);
        setLoading(true);

        const timeoutId = setTimeout(() => fetchJobs(1, true), 300);
        return () => clearTimeout(timeoutId);
    }, [fetchJobs]);

    // Načítání dalších stránek
    useEffect(() => {
        if (page > 1) fetchJobs(page, false);
    }, [page, fetchJobs]);

    // INFINITE SCROLL OBSERVER
    const observer = useRef();
    const lastJobRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMore) {
                        setPage((prev) => prev + 1);
                    }
                },
                { rootMargin: "0px 0px 200px 0px", threshold: 0.1 },
            );
            if (node) observer.current.observe(node);
        },
        [loading, hasMore],
    );

    useEffect(() => {
        const performReset = () => {
            setSearchTerm("");
            setLocation(null);
            setRadius(DEFAULT_RADIUS);
            setIsOpenLicense(false);
            setDriverLicense("all");
            setDateFrom(format(new Date(), "yyyy-MM-dd"));
            setDateTo("");
            setDurationType({ value: "single", label: "Jednorázová akce" });
            setDisplayMode({ value: "all", label: "Zobrazit vše" });
            setNetworkOnly(false);
            setVerifiedOnly(false);
            setPage(1);
        };

        window.addEventListener("reset-filters", performReset);
        return () => window.removeEventListener("reset-filters", performReset);
    }, []);

    // Zavření dropdownů při kliknutí mimo
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpenLicense && !selectRefLicense.current?.contains(event.target))
                setIsOpenLicense(false);
            if (isOpenDuration && !selectRefDuration.current?.contains(event.target))
                setIsOpenDuration(false);
            if (isOpenDisplay && !selectRefDisplay.current?.contains(event.target))
                setIsOpenDisplay(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpenLicense, isOpenDuration, isOpenDisplay]);

    const handleItemClick = useCallback(
        (id) => {
            navigate(`/jobs/item/${id}`, {
                state: {
                    fromCategory: subcategory,
                    fromMode: "jobs",
                },
            });
        },
        [navigate, subcategory],
    );

    // Definice povolených podkategorií (stejné jako u pracovníků)
    const allowedSubcategories = [
        "light_technician",
        "sound_technician",
        "av_technician",
        "rigger",
        "stagehands",
    ];

    if (subcategory && !allowedSubcategories.includes(subcategory)) {
        return <Navigate to={buildRoute(ROUTES.NOT_FOUND)} replace />;
    }

    const titleHero = !subcategory
        ? categoryMap[path.pathname.split("/")[1]]
        : categoryMap[subcategory];

    return (
        <>
            <Path mode="jobs" category={subcategory} />
            <h1 className="list_title">{titleHero}</h1>

            <div className="search_bar-container">
                <div className="search_bar-box">
                    <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="search_bar-icon"
                    />
                    <input
                        className="search_bar"
                        placeholder="Vyhledávejte"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {!subcategory && (
                <div className="workers-nav">
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faLightbulb} />}
                        text="Osvětlovač"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.JOBS_CATEGORY, {
                                    subcategory: "light_technician",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="100"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="icon icon-tabler icons-tabler-filled icon-tabler-device-speaker"
                            >
                                <path
                                    stroke="none"
                                    d="M0 0h24v24H0z"
                                    fill="none"
                                />
                                <path d="M17 2a3 3 0 0 1 3 3v14a3 3 0 0 1 -3 3h-10a3 3 0 0 1 -3 -3v-14a3 3 0 0 1 3 -3zm-5 9a4 4 0 0 0 -3.995 3.8l-.005 .2a4 4 0 1 0 4 -4m0 -5a1 1 0 0 0 -1 1v.01a1 1 0 0 0 2 0v-.01a1 1 0 0 0 -1 -1" />
                            </svg>
                        }
                        text="Zvukař"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.JOBS_CATEGORY, {
                                    subcategory: "sound_technician",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faVideo} />}
                        text="AV Technik"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.JOBS_CATEGORY, {
                                    subcategory: "av_technician",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faWrench} />}
                        text="Rigger"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.JOBS_CATEGORY, {
                                    subcategory: "rigger",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faHandFist} />}
                        text="Stagehands"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.JOBS_CATEGORY, {
                                    subcategory: "stagehands",
                                }),
                            )
                        }
                    />
                </div>
            )}

            <div className="list-container">
                <div className="all-filters">
                    <div className="filters">

                        {/* ZOBRAZIT (profile_match)
                        <div className="location" ref={selectRefDisplay}>
                            <div className="custom-select-wrapper">
                                <label
                                    htmlFor="display_mode"
                                    className="body_base label-move"
                                >
                                    Zobrazit
                                </label>
                                <div
                                    className={`custom-select-down ${isOpenDisplay ? "open" : ""}`}
                                    onClick={() => setIsOpenDisplay(!isOpenDisplay)}
                                >
                                    <span className="selected">
                                        {displayMode.label}
                                    </span>
                                    <span
                                        className={`arrow ${isOpenDisplay ? "rotate" : ""}`}
                                    >
                                        ▼
                                    </span>
                                </div>
                                {isOpenDisplay && (
                                    <div className="options-down">
                                        {DISPLAY_OPTIONS.map((opt) => (
                                            <div
                                                key={opt.value}
                                                className={`option ${displayMode.value === opt.value ? "selected" : ""}`}
                                                onClick={() => {
                                                    setDisplayMode(opt);
                                                    setIsOpenDisplay(false);
                                                }}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>*/}

                        {/* SPOLUPRÁCE (duration_type) */}
                        <div className="location" ref={selectRefDuration}>
                            <div className="custom-select-wrapper">
                                <label
                                    htmlFor="duration_type"
                                    className="body_base label-move"
                                >
                                    Spolupráce
                                </label>
                                <div
                                    className={`custom-select-down ${isOpenDuration ? "open" : ""}`}
                                    onClick={() => setIsOpenDuration(!isOpenDuration)}
                                >
                                    <span className="selected">
                                        {durationType.label}
                                    </span>
                                    <span
                                        className={`arrow ${isOpenDuration ? "rotate" : ""}`}
                                    >
                                        ▼
                                    </span>
                                </div>
                                {isOpenDuration && (
                                    <div className="options-down">
                                        {DURATION_OPTIONS.map((opt) => (
                                            <div
                                                key={opt.value}
                                                className={`option ${durationType.value === opt.value ? "selected" : ""}`}
                                                onClick={() => {
                                                    setDurationType(opt);
                                                    setIsOpenDuration(false);
                                                }}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* LOKALITA */}
                        <div className="location">
                            <label
                                htmlFor="filter_location"
                                className="body_base label-move"
                            >
                                Lokalita
                            </label>
                            <LocationAutocomplete
                                id="filter_location"
                                value={location}
                                onChange={setLocation}
                                placeholder="Celá ČR"
                            />
                            {location?.latitude && (
                                <RadiusSelect
                                    value={radius}
                                    onChange={setRadius}
                                />
                            )}
                        </div>

                        {/* DATUM OD / DO */}
                        <DateRangePicker
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            onChangeFrom={setDateFrom}
                            onChangeTo={setDateTo}
                            durationType={durationType.value}
                        />

                        {/* ŘIDIČSKÝ PRŮKAZ */}
                        <div className="location" ref={selectRefLicense}>
                            <div className="custom-select-wrapper">
                                <label
                                    htmlFor="driver_license"
                                    className="body_base label-move"
                                >
                                    Řidičský průkaz
                                </label>
                                <div
                                    className={`custom-select-down ${isOpenLicense ? "open" : ""}`}
                                    onClick={() => setIsOpenLicense(!isOpenLicense)}
                                >
                                    <span className="selected">
                                        {LICENSE_OPTIONS.find(
                                            (opt) =>
                                                opt.value === driverLicense,
                                        )?.label || "Nerozhoduje"}
                                    </span>
                                    <span
                                        className={`arrow ${isOpenLicense ? "rotate" : ""}`}
                                    >
                                        ▼
                                    </span>
                                </div>
                                {isOpenLicense && (
                                    <div className="options-down">
                                        {LICENSE_OPTIONS.map((opt) => (
                                            <div
                                                key={opt.value || "all"}
                                                className={`option ${driverLicense === opt.value ? "selected" : ""}`}
                                                onClick={() => {
                                                    setDriverLicense(opt.value);
                                                    setIsOpenLicense(false);
                                                }}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* KOMUNITA */}
                        <label
                            htmlFor="network_only"
                            className="body_base label-move"
                        >
                            Komunita
                        </label>
                        <div
                            id="network_only"
                            className="checkbox-container checkbox-container-filter"
                        >
                            <input
                                type="checkbox"
                                id="network_only-checkbox"
                                className="custom-checkbox"
                                checked={networkOnly}
                                onChange={() => setNetworkOnly(!networkOnly)}
                            />
                            <label htmlFor="network_only-checkbox" className="checkbox-text">
                                Organizátor doporučen mými uloženými
                            </label>
                        </div>

                        <div
                            id="verified_only"
                            className="checkbox-container checkbox-container-filter"
                        >
                            <input
                                type="checkbox"
                                id="verified_only-checkbox"
                                className="custom-checkbox"
                                checked={verifiedOnly}
                                onChange={() => setVerifiedOnly(!verifiedOnly)}
                            />
                            <label htmlFor="verified_only-checkbox" className="checkbox-text">
                                Ověření majitelé
                            </label>
                        </div>
                    </div>
                </div>

                <div className="all-items-full job-items">
                    {!loading && (
                        <div className="results-count">
                            {jobs.reduce((total, job) => total + (job.positions?.length || 0), 0)}
                        </div>
                    )}
                    {(() => {
                        const cards = jobs.flatMap((job) => {
                            return (job.positions || []).map((position) => {
                                const startsOn = position.starts_on;
                                const endsOn = position.ends_on;
                                const startsAt = startsOn || job.starts_at;
                                const endsAt = endsOn || job.ends_at;
                                const startsTime = position.starts_time || job.starts_time;
                                const endsTime = position.ends_time || job.ends_time;
                                const payAmount = Number(position.pay_amount);
                                const payMax = position.pay_max ? Number(position.pay_max) : null;
                                const payType = position.pay_type;

                                return {
                                    job,
                                    position,
                                    role: position.role,
                                    roleLabel: position.role_label,
                                    startsAt,
                                    startsTime,
                                    endsAt,
                                    endsTime,
                                    payMin: payAmount,
                                    payMax,
                                    payType,
                                    capacity: position.capacity,
                                    isSos: position.priority_offer,
                                };
                            });
                        });

                        cards.sort((a, b) => {
                            const dateA = a.startsAt || "";
                            const dateB = b.startsAt || "";
                            if (dateA !== dateB) return dateA.localeCompare(dateB);
                            const timeA = a.startsTime || "";
                            const timeB = b.startsTime || "";
                            return timeA.localeCompare(timeB);
                        });

                        const groups = [];
                        const groupMap = {};
                        cards.forEach((card) => {
                            const dateKey = card.startsAt || "";
                            if (!groupMap[dateKey]) {
                                groupMap[dateKey] = [];
                                groups.push({ dateKey, cards: groupMap[dateKey] });
                            }
                            groupMap[dateKey].push(card);
                        });

                        const totalCards = cards.length;
                        let cardIndex = 0;

                        return groups.map((group) => (
                            <div key={group.dateKey} className="job_item_day_group">
                                <div className="job_item_day_header">{formatDayHeader(group.dateKey)}</div>
                                {group.cards.map((card) => {
                                    const isLast = cardIndex === totalCards - 1;
                                    cardIndex++;
                                    return (
                                        <JobItem
                                            key={`${card.job.id}-${card.position.id}`}
                                            id={card.job.id}
                                            ref={isLast ? lastJobRef : null}
                                            isFavouriteInitially={card.job.is_favourite}
                                            title={card.job.title}
                                            starts_at={card.startsAt}
                                            starts_time={card.startsTime}
                                            ends_at={card.endsAt}
                                            ends_time={card.endsTime}
                                            city={card.job.city}
                                            location_name={card.job.location_name}
                                            organizerName={card.job.user_name}
                                            organizerImage={card.job.user_profile_image_url}
                                            organizerRating={card.job.user_review_value}
                                            organizerVerified={card.job.user_is_verified}
                                            isSos={card.isSos}
                                            specialization={
                                                card.roleLabel ||
                                                categoryMap[card.role] ||
                                                card.role
                                            }
                                            payMin={card.payMin}
                                            payMax={card.payMax}
                                            payType={card.payType}
                                            onClick={() => handleItemClick(card.job.id)}
                                        />
                                    );
                                })}
                            </div>
                        ));
                    })()}

                    {/* Načítací skeletony */}
                    {loading &&
                        Array.from({ length: 4 }).map((_, index) => (
                            <JobItemSkeleton key={`skeleton-${index}`} />
                        ))}

                    {/* Prázdný stav */}
                    {!loading && jobs.length === 0 && (
                        <div className="no-results_listing-container">
                            <h2 className="no-results_listing">
                                Nebyly nalezeny žádné pracovní nabídky.
                            </h2>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Jobs;
